import { Controller, type Control, type UseFormRegister, type UseFormSetValue } from 'react-hook-form';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { SampleFormData } from '../../utils/sampleFormSchema';
import { SampleFormSectionTitle } from './SampleFormSectionTitle';

type PathogenOption = 'included' | 'excluded';

interface SampleFormPathogenFieldsProps {
  pathogenOption: PathogenOption;
  onPathogenOptionChange: (value: PathogenOption) => void;
  register: UseFormRegister<SampleFormData>;
  control: Control<SampleFormData>;
  setValue: UseFormSetValue<SampleFormData>;
  /** details 内に埋め込むとき、外枠・見出しを省略 */
  embedded?: boolean;
}

/** サンプル登録・編集モーダルの病原体セクション */
export default function SampleFormPathogenFields({
  pathogenOption,
  onPathogenOptionChange,
  register,
  control,
  setValue,
  embedded = false,
}: SampleFormPathogenFieldsProps) {
  const { t } = useTranslation();

  const fields = (
    <>
      <fieldset className="mb-3 border-0 p-0">
        <legend className="form-label fs-6 mb-2">
          {t('sample.pathogen_option_legend')}
        </legend>
        <Form.Check
          inline
          type="radio"
          id="pathogen-included"
          name="pathogenOption"
          label={
            <span
              title={t('sample.pathogen_option_included_title', {
                defaultValue: '属・種などの病原体情報を入力する',
              })}
            >
              {t('sample.pathogen_option_included')}
            </span>
          }
          checked={pathogenOption === 'included'}
          onChange={() => {
            onPathogenOptionChange('included');
            setValue('pathogenOption', 'included');
          }}
        />
        <Form.Check
          inline
          type="radio"
          id="pathogen-excluded"
          name="pathogenOption"
          label={
            <span
              title={t('sample.pathogen_option_excluded_title', {
                defaultValue: '病原体情報を入力しない',
              })}
            >
              {t('sample.pathogen_option_excluded')}
            </span>
          }
          checked={pathogenOption === 'excluded'}
          onChange={() => {
            onPathogenOptionChange('excluded');
            setValue('pathogenOption', 'excluded');
          }}
        />
      </fieldset>

      {pathogenOption === 'included' ? (
        <div id="pathogen-form">
          <div className="row">
            <Form.Group className="mb-3 col-md-6">
              <Form.Label htmlFor="sample-pathogen-genus">{t('sample.genus')}</Form.Label>
              <Form.Control
                id="sample-pathogen-genus"
                name="pathogenGenus"
                type="text"
                {...register('pathogenGenus')}
                autoComplete="off"
                spellCheck={false}
                autoCorrect="off"
                autoCapitalize="off"
              />
            </Form.Group>
            <Form.Group className="mb-3 col-md-6">
              <Form.Label htmlFor="sample-pathogen-species">{t('sample.species')}</Form.Label>
              <Form.Control
                id="sample-pathogen-species"
                name="pathogenSpecies"
                type="text"
                {...register('pathogenSpecies')}
                autoComplete="off"
                spellCheck={false}
                autoCorrect="off"
                autoCapitalize="off"
              />
            </Form.Group>
          </div>
          <div className="row">
            <Form.Group className="mb-3 col-md-6">
              <Form.Label htmlFor="sample-pathogen-subspecies">{t('sample.subspecies')}</Form.Label>
              <Form.Control
                id="sample-pathogen-subspecies"
                name="pathogenSubspecies"
                type="text"
                {...register('pathogenSubspecies')}
                autoComplete="off"
                spellCheck={false}
                autoCorrect="off"
                autoCapitalize="off"
              />
            </Form.Group>
            <Form.Group className="mb-3 col-md-6">
              <Form.Label htmlFor="sample-pathogen-strain">{t('sample.strain')}</Form.Label>
              <Form.Control
                id="sample-pathogen-strain"
                name="pathogenStrain"
                type="text"
                {...register('pathogenStrain')}
                autoComplete="off"
                spellCheck={false}
                autoCorrect="off"
                autoCapitalize="off"
              />
            </Form.Group>
          </div>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="sample-pathogen-note">{t('sample.pathogen_note')}</Form.Label>
            <Controller
              name="pathogenNote"
              control={control}
              render={({ field }) => (
                <Form.Control
                  as="textarea"
                  id="sample-pathogen-note"
                  name="pathogenNote"
                  rows={2}
                  {...field}
                  value={field.value ?? ''}
                  autoComplete="off"
                />
              )}
            />
          </Form.Group>
        </div>
      ) : null}
    </>
  );

  if (embedded) return fields;

  return (
    <div className="sample-form-section">
      <SampleFormSectionTitle icon="fa-virus">
        {t('sample.pathogen_information')}
      </SampleFormSectionTitle>
      {fields}
    </div>
  );
}
